import { collection, deleteDoc, doc, getCountFromServer, getDoc, getDocs, getFirestore, limit, onSnapshot, orderBy, query, setDoc, startAfter, updateDoc, where } from "firebase/firestore";
//#region src/FirebaseDatabaseProvider.ts
/**
* Firebase Firestore implementation of IDatabaseProvider
*/
var FirebaseDatabaseProvider = class {
	/**
	* @param app Initialized Firebase app
	* @param databaseId Named Firestore database; omit for the default one.
	*   A project can hold several databases — e.g. one for user-owned data and
	*   one for a public, read-only dataset with its own rules. Which one is
	*   addressed is a decision of the app, not of every call site.
	*/
	constructor(app, databaseId) {
		this.db = databaseId ? getFirestore(app, databaseId) : getFirestore(app);
	}
	/** Translate the provider-neutral constraints into Firestore constraints. */
	toFirestoreConstraints(constraints) {
		return (constraints || []).map((constraint) => {
			if (constraint.type === "where") return where(constraint.field, constraint.operator, constraint.value);
			throw new Error(`Unknown constraint type: ${constraint.type}`);
		});
	}
	async getCollection(collectionPath, options) {
		const constraints = this.toFirestoreConstraints(options?.constraints);
		for (const order of options?.orderBy || []) constraints.push(orderBy(order.field, order.direction ?? "asc"));
		if (options?.startAfter) {
			const cursorSnap = await getDoc(doc(this.db, collectionPath, options.startAfter));
			if (cursorSnap.exists()) constraints.push(startAfter(cursorSnap));
		}
		const pageSize = options?.limit;
		if (pageSize) constraints.push(limit(pageSize + 1));
		const docs = (await getDocs(query(collection(this.db, collectionPath), ...constraints))).docs;
		const hasMore = pageSize !== void 0 && docs.length > pageSize;
		const seite = hasMore ? docs.slice(0, pageSize) : docs;
		return {
			documents: seite.map((d) => ({
				id: d.id,
				data: d.data(),
				exists: true
			})),
			nextCursor: hasMore ? seite[seite.length - 1]?.id : void 0
		};
	}
	onCollection(collectionPath, callback, options) {
		const firestoreConstraints = this.toFirestoreConstraints(options?.constraints);
		for (const order of options?.orderBy || []) firestoreConstraints.push(orderBy(order.field, order.direction ?? "asc"));
		if (options?.limit !== void 0) firestoreConstraints.push(limit(options.limit));
		const collectionRef = collection(this.db, collectionPath);
		const q = firestoreConstraints.length > 0 ? query(collectionRef, ...firestoreConstraints) : collectionRef;
		return onSnapshot(q, (querySnapshot) => {
			callback(querySnapshot.docs.map((doc) => ({
				id: doc.id,
				data: doc.data(),
				exists: doc.exists()
			})));
		}, (error) => {
			if (options?.onError) options.onError(error);
			else console.error("Firestore collection listener error:", error);
		});
	}
	async getCollectionCount(collectionPath, options) {
		return (await getCountFromServer(query(collection(this.db, collectionPath), ...this.toFirestoreConstraints(options?.constraints)))).data().count;
	}
	async getDocument(collectionPath, documentId) {
		const docRef = doc(this.db, collectionPath, documentId);
		const docSnap = await getDoc(docRef);
		return {
			id: docSnap.id,
			data: docSnap.data() ?? {},
			exists: docSnap.exists()
		};
	}
	async setDocument(collectionPath, documentId, data) {
		const docRef = doc(this.db, collectionPath, documentId);
		await setDoc(docRef, data);
	}
	async updateDocument(collectionPath, documentId, data) {
		const docRef = doc(this.db, collectionPath, documentId);
		await updateDoc(docRef, data);
	}
	async deleteDocument(collectionPath, documentId) {
		const docRef = doc(this.db, collectionPath, documentId);
		await deleteDoc(docRef);
	}
	/**
	* Get the underlying Firestore instance
	* (For Firestore-specific operations)
	*/
	getFirestore() {
		return this.db;
	}
};
//#endregion
export { FirebaseDatabaseProvider };
